import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';

interface LiquidGlassModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    maxWidth?: string;
}

const LiquidGlassModal: React.FC<LiquidGlassModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = 'max-w-md'
}) => {
    const { actualTheme } = useTheme();

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    />

                    {/* Modal Container */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className={`relative w-full ${maxWidth} overflow-hidden rounded-3xl border shadow-2xl transition-all duration-300 ${actualTheme === 'dark'
                                ? 'bg-slate-900/40 border-slate-500/30 text-slate-100'
                                : 'bg-white/40 border-white/40 text-neutral-900'
                            } backdrop-blur-xl liquid-glass`}
                    >
                        {/* Glossy Header Effect */}
                        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />

                        <div className="relative p-6 sm:p-8">
                            {/* Close Button */}
                            <button
                                onClick={onClose}
                                className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${actualTheme === 'dark'
                                        ? 'hover:bg-slate-800/50 text-slate-400'
                                        : 'hover:bg-black/5 text-neutral-500'
                                    }`}
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>

                            {title && (
                                <h3 className="text-xl sm:text-2xl font-bold mb-4 pr-10">
                                    {title}
                                </h3>
                            )}

                            <div className="relative z-10">
                                {children}
                            </div>
                        </div>

                        {/* Bottom Shine */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent" />
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default LiquidGlassModal;
